import { ChevronRight, FileBarChart, Folder, FolderOpen, Lock } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { isBuilt } from '@/components/reports/builtReports'
import { REPORT_CATEGORIES, reportPath, type ReportCategory } from '@/lib/reportCatalog'

/**
 * The report tree: five topics, each expanding to its reports.
 *
 * Names only — a description per row would turn a scannable index into a wall
 * of prose. The description belongs in the detail header, where there is one of
 * it; here it is the row's tooltip.
 *
 * Every row is a real `<Link>`, so a report can be middle-clicked, bookmarked
 * and shared. The current query string rides along, which is what keeps the
 * period from resetting as the reader moves between reports.
 */
export function ReportTopicTree({
  openTopics,
  activeReportId,
  search,
  onToggleTopic,
}: {
  openTopics: ReadonlySet<string>
  /** Catalog id of the selected report, e.g. '3.1'. */
  activeReportId: string | null
  /** Query string carrying the period, appended to every link. */
  search: string
  onToggleTopic: (slug: string) => void
}) {
  return (
    <nav className='flex flex-col' aria-label='Reports'>
      {REPORT_CATEGORIES.map((category) => (
        <TopicBranch
          key={category.slug}
          category={category}
          open={openTopics.has(category.slug)}
          activeReportId={activeReportId}
          search={search}
          onToggle={() => onToggleTopic(category.slug)}
        />
      ))}
    </nav>
  )
}

function TopicBranch({
  category,
  open,
  activeReportId,
  search,
  onToggle,
}: {
  category: ReportCategory
  open: boolean
  activeReportId: string | null
  search: string
  onToggle: () => void
}) {
  const holdsActive = category.reports.some((r) => r.id === activeReportId)
  const FolderIcon = open ? FolderOpen : Folder
  // How much of this section can actually be run. Shown on the folder so a
  // reader sees which sections are done without opening any of them — the
  // whole point of the intensity below.
  const built = category.reports.filter((r) => isBuilt(r.id)).length
  const total = category.reports.length

  return (
    <div>
      <button
        type='button'
        onClick={onToggle}
        aria-expanded={open}
        title={category.description}
        className={cn(
          'flex w-full items-center gap-1 rounded px-1 py-1.5 text-left text-sm transition-colors hover:bg-muted',
          // Full ink once anything in the section is runnable; grey while the
          // whole section is still to come.
          built === 0 ? 'text-muted-foreground/70' : 'text-foreground',
          holdsActive && 'font-medium',
        )}
      >
        <ChevronRight
          className={cn('h-3 w-3 shrink-0 opacity-50 transition-transform', open && 'rotate-90')}
        />
        <FolderIcon className='h-3.5 w-3.5 shrink-0 text-muted-foreground' />
        <span className='truncate'>
          {category.id}. {category.name}
        </span>
        <span
          className={cn(
            'ml-auto shrink-0 pl-1 text-[10px] tabular-nums',
            built === total ? 'text-foreground/60' : 'text-muted-foreground/70',
          )}
          title={`${built} of ${total} available`}
        >
          {built}/{total}
        </span>
      </button>

      {open && (
        <ul>
          {category.reports.map((report) => {
            const active = report.id === activeReportId
            const runnable = isBuilt(report.id)
            const blocked = report.status.kind === 'blocked'
            return (
              <li key={report.slug}>
                <Link
                  to={{ pathname: reportPath(category, report), search }}
                  aria-current={active ? 'page' : undefined}
                  title={report.description}
                  className={cn(
                    'flex items-center gap-1.5 rounded py-1.5 pl-6 pr-1 text-sm transition-colors',
                    active && 'bg-primary/10 font-medium text-primary',
                    // Three levels of ink, darkest = usable. Built reports read
                    // as the content of this tree; the rest read as a roadmap,
                    // so nobody has to click one to find out it is empty.
                    !active && runnable && 'text-foreground hover:bg-muted',
                    !active && !runnable && !blocked && 'text-muted-foreground/60 hover:bg-muted',
                    // Lightest: not merely unbuilt but unbuildable.
                    !active && blocked && 'text-muted-foreground/40 hover:bg-muted',
                  )}
                >
                  <FileBarChart
                    className={cn('h-3.5 w-3.5 shrink-0', runnable ? 'opacity-80' : 'opacity-40')}
                  />
                  <span className='truncate'>
                    {report.id.split('.')[1]}. {report.name}
                  </span>
                  {blocked && (
                    // Flagged in the tree, not only on open: a reader scanning
                    // the index should see which two cannot be run at all.
                    <Lock className='ml-auto h-3 w-3 shrink-0 opacity-60' aria-label='No data source' />
                  )}
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
